import { useEffect, useRef, useState } from 'react';
import ContactForm from '../components/ContactForm';
import useScrollReveal from '../hooks/useScrollReveal';
import './ContactPage.css';

const contactTopics = [
  {
    number: '01',
    title: 'Product questions',
    description:
      'Questions about Rusiv, its capabilities, availability, or how it works.',
    subject: 'general-inquiry',
  },
  {
    number: '02',
    title: 'Partnerships & Investment',
    description:
      'Incubation, investment interest, collaboration, or research partnerships.',
    subject: 'partnership',
  },
  {
    number: '03',
    title: 'Careers & Everything Else',
    description:
      'Career interest, feedback, or anything else on your mind.',
    subject: 'career',
  },
];

const ContactPage = () => {
  const [mounted, setMounted] = useState(false);
  const [presetSubject, setPresetSubject] = useState('');
  const formWrapperRef = useRef(null);

  const handleTopicSelect = (subject) => {
    setPresetSubject(subject);
    formWrapperRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useScrollReveal({
    threshold: 0.1,
    rootMargin: '-50px',
    oneWay: true,
  });

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 60);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });

    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="ctp-page">
      {/* Hero */}
      <section className="ctp-hero">
        <div className="container-narrow">
          <span
            className="ctp-eyebrow"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'none' : 'translateY(24px)',
              transition: 'all 0.9s var(--ease-out) 0.1s',
            }}
          >
            Contact Allyverto
          </span>

          <h1
            className="ctp-hero-title"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'none' : 'translateY(50px)',
              transition: 'all 1.1s var(--ease-out) 0.2s',
            }}
          >
            Let&apos;s start a
            <br />
            <em>conversation.</em>
          </h1>

          <p
            className="ctp-hero-sub"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'none' : 'translateY(40px)',
              transition: 'all 1.1s var(--ease-out) 0.4s',
            }}
          >
            Have a question, an idea, or something you would like to discuss?
            Send us a message using the form below.
          </p>
        </div>
      </section>

      {/* Contact content */}
      <section className="ctp-main">
        <div className="container">
          <div className="ctp-grid">
            {/* Information */}
            <div className="ctp-info-column" data-scroll>
              <span className="ctp-section-label">How we can help</span>

              <h2 className="ctp-info-heading">
                Tell us what&apos;s
                <br />
                <em>on your mind.</em>
              </h2>

              <p className="ctp-info-text">
                Choose the subject that best matches your message and provide
                as much useful context as possible. This helps us understand
                your enquiry and direct it appropriately.
              </p>

              <div className="ctp-topic-list">
                {contactTopics.map((topic) => (
                  <button
                    type="button"
                    className="ctp-topic-item"
                    key={topic.number}
                    onClick={() => handleTopicSelect(topic.subject)}
                  >
                    <span className="ctp-topic-number">{topic.number}</span>

                    <div>
                      <h3 className="ctp-topic-title">{topic.title}</h3>
                      <p className="ctp-topic-description">
                        {topic.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              <div className="ctp-privacy-note">
                <div className="ctp-privacy-icon" aria-hidden="true">
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 3L5 6v5c0 4.7 2.8 8.2 7 10 4.2-1.8 7-5.3 7-10V6l-7-3Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    <path
                      d="m9.5 12 1.6 1.6 3.7-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <div>
                  <p className="ctp-privacy-title">Share only what is needed</p>
                  <p className="ctp-privacy-text">
                    Please do not include passwords, payment information, or
                    other highly sensitive personal details.
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div
              className="ctp-form-wrapper"
              data-scroll
              style={{ transitionDelay: '0.12s' }}
              ref={formWrapperRef}
            >
              <div className="ctp-form-col">
                <div className="ctp-form-header">
                  <span className="ctp-form-label">Contact form</span>
                  <h2 className="ctp-form-title">Send us a message</h2>
                  <p className="ctp-form-description">
                    Complete the form and our team will review your enquiry.
                  </p>
                </div>

                <ContactForm idPrefix="page" presetSubject={presetSubject} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom principles */}
      <section className="ctp-bottom">
        <div className="container">
          <div className="ctp-bottom-header" data-scroll>
            <span className="ctp-section-label">What to expect</span>
            <h2 className="ctp-bottom-title">
              A simple and thoughtful
              <br />
              contact experience.
            </h2>
          </div>

          <div className="ctp-promise-row">
            <article data-scroll>
              <p className="ctp-promise-num">01</p>
              <h3 className="ctp-promise-title">Clear communication</h3>
              <p className="ctp-promise-desc">
                Your message should tell us what you need, why it matters, and
                any important context we should understand.
              </p>
            </article>

            <article data-scroll style={{ transitionDelay: '0.1s' }}>
              <p className="ctp-promise-num">02</p>
              <h3 className="ctp-promise-title">Thoughtful review</h3>
              <p className="ctp-promise-desc">
                Every enquiry is reviewed so it can be understood and directed
                to the appropriate part of the team.
              </p>
            </article>

            <article data-scroll style={{ transitionDelay: '0.2s' }}>
              <p className="ctp-promise-num">03</p>
              <h3 className="ctp-promise-title">Privacy awareness</h3>
              <p className="ctp-promise-desc">
                We encourage you to provide only the information necessary for
                us to understand and respond to your enquiry.
              </p>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;